import { Statuses } from '@geee-be/core';
import {
  captureException,
  captureMessage,
  type Span,
  startInactiveSpan,
} from '@sentry/browser';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { jsonDateParser } from 'json-date-parser';

const SPAN_STATUS_OK = 1;
const SPAN_STATUS_ERROR = 2;

interface MaybeWithSpan {
  span?: Span;
}

axios.defaults.transformResponse = [
  (response: string) =>
    ['[', '{'].includes(response.charAt(0))
      ? (JSON.parse(response, jsonDateParser) as Record<string, unknown>)
      : response,
];

axios.interceptors.request.use((config) => {
  const span = startInactiveSpan({
    name: config.url ?? '<unknown>',
    op: 'http.request',
  });
  config.headers['sentry-trace'] = span.spanContext().traceId;
  config.headers['sentry-span'] = span.spanContext().spanId;
  (config as MaybeWithSpan).span = span;
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const { span } = response.config as MaybeWithSpan;
    if (response.status === Number(Statuses.BAD_REQUEST)) {
      const extra = response.data ? { data: response.data as unknown } : {};
      captureMessage('Bad Request (status=400)', { extra });
    }
    span?.setStatus({ code: SPAN_STATUS_OK });
    span?.end();
    return response;
  },
  (error: AxiosError) => {
    if (error.message === 'Network Error') {
      return Promise.reject(error);
    }
    const { config } = error;
    if (
      config?.url !== '/api/v1/token/refresh' ||
      error.status !== Statuses.UNAUTHORIZED
    ) {
      const extra =
        error.status === Statuses.BAD_REQUEST
          ? { data: error.response?.data }
          : {};
      captureException(error, { extra });
    }
    if (config) {
      const { span } = config as MaybeWithSpan;
      span?.setStatus({ code: SPAN_STATUS_ERROR, message: error.message });
      span?.end();
    }
    return Promise.reject(error);
  },
);
